import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useDivision } from "@/context/DivisionContext";
import { 
  CheckCircle, Trash2, AlertTriangle, Download, Clock, Calendar,
  DollarSign, Receipt, FileText
} from "lucide-react";
import { getDistrictByCounty, getAllDistricts } from "@/utils/txdotDistricts";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AccountsReceivable = () => {
  const { division } = useDivision();
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [districtFilter, setDistrictFilter] = useState('all');
  const [sortBy, setSortBy] = useState('invoice_date');
  const [sortOrder, setSortOrder] = useState('asc');
  const { getAuthHeaders } = useAuth();

  // Mark as Paid Dialog state
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [paymentData, setPaymentData] = useState({
    payment_received_date: new Date().toISOString().split('T')[0],
    payment_amount: '',
    payment_notes: ''
  });

  useEffect(() => {
    fetchARProjects();
  }, [division]);

  const fetchARProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`, { 
        headers: getAuthHeaders(),
        params: { division }
      });
      // Filter for invoiced but not paid projects (status = "invoiced")
      const arProjects = response.data.filter(p => p.status === 'invoiced');
      setProjects(arProjects);
      setFilteredProjects(arProjects);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching AR projects:", error);
      toast.error("Failed to load accounts receivable");
      setLoading(false);
    }
  };

  // Filter and sort
  useEffect(() => {
    let filtered = [...projects];

    if (searchTerm) {
      filtered = filtered.filter(project => {
        const name = project.project_name || '';
        const county = project.county || '';
        const contractor = project.contractor_name || '';
        const invoiceNum = project.invoice_number || '';
        const searchLower = searchTerm.toLowerCase();
        
        return name.toLowerCase().includes(searchLower) ||
               county.toLowerCase().includes(searchLower) ||
               contractor.toLowerCase().includes(searchLower) ||
               invoiceNum.toLowerCase().includes(searchLower);
      });
    }

    if (division === 'texas' && districtFilter !== 'all') {
      filtered = filtered.filter(project => {
        const district = getDistrictByCounty(project.county);
        return district === districtFilter;
      });
    }

    filtered.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'project_name':
          aVal = a.project_name || '';
          bVal = b.project_name || '';
          break;
        case 'contractor':
          aVal = a.contractor_name || '';
          bVal = b.contractor_name || '';
          break;
        case 'amount':
          aVal = a.bid_amount || 0;
          bVal = b.bid_amount || 0;
          break;
        case 'invoice_date':
          aVal = a.invoice_date || '9999-99-99';
          bVal = b.invoice_date || '9999-99-99';
          break;
        case 'days_outstanding':
          aVal = getDaysOutstanding(a.invoice_date);
          bVal = getDaysOutstanding(b.invoice_date);
          break;
        default:
          aVal = a.project_name || '';
          bVal = b.project_name || '';
      }

      if (typeof aVal === 'string') {
        return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      } else {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
    });

    setFilteredProjects(filtered);
  }, [projects, searchTerm, districtFilter, sortBy, sortOrder, division]);

  const getDaysOutstanding = (invoiceDate) => {
    if (!invoiceDate) return 0;
    const invoice = new Date(invoiceDate);
    const today = new Date();
    return Math.floor((today - invoice) / (1000 * 60 * 60 * 24));
  };

  const getAgingCategory = (days) => {
    if (days <= 30) return { label: 'Current', color: 'text-[#10b981]', bg: 'bg-[#10b981]/20' };
    if (days <= 60) return { label: '31-60 Days', color: 'text-yellow-500', bg: 'bg-yellow-500/20' };
    if (days <= 90) return { label: '61-90 Days', color: 'text-orange-500', bg: 'bg-orange-500/20' };
    return { label: '90+ Days', color: 'text-red-500', bg: 'bg-red-500/20' };
  };

  const exportToExcel = () => {
    const csvData = filteredProjects.map(project => ({
      'Project Name': project.project_name || '',
      'County': project.county || '',
      'Control Number': project.control_number || '',
      'Contractor': project.contractor_name || '',
      'Invoice Number': project.invoice_number || '',
      'Invoice Date': project.invoice_date || '',
      'Amount': project.bid_amount || 0,
      'Days Outstanding': getDaysOutstanding(project.invoice_date),
      'District': division === 'texas' ? getDistrictByCounty(project.county) : 'N/A'
    }));

    const csv = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `accounts-receivable-${division}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Exported to CSV successfully!');
  };

  const openPaymentDialog = (project) => {
    setSelectedProject(project);
    setPaymentData({
      payment_received_date: new Date().toISOString().split('T')[0],
      payment_amount: project.bid_amount || '',
      payment_notes: ''
    });
    setPaymentDialogOpen(true);
  };

  const markAsPaid = async () => {
    if (!selectedProject) return;

    try {
      await axios.patch(`${API}/projects/${selectedProject.id}/status`, 
        { 
          status: 'paid',
          payment_received_date: paymentData.payment_received_date,
          payment_amount: parseFloat(paymentData.payment_amount) || selectedProject.bid_amount,
          payment_notes: paymentData.payment_notes
        },
        { headers: getAuthHeaders() }
      );
      toast.success("Payment recorded - Project moved to Completed");
      setPaymentDialogOpen(false);
      setSelectedProject(null);
      fetchARProjects();
    } catch (error) {
      console.error("Error marking invoice as paid:", error);
      toast.error("Failed to record payment");
    }
  };

  const handleDeleteProject = async (projectId) => {
    try {
      await axios.delete(`${API}/projects/${projectId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Project deleted successfully');
      setDeleteConfirm(null);
      fetchARProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete project');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US');
  };

  if (loading) {
    return <div className="text-white p-8">Loading...</div>;
  }

  const totalAR = projects.reduce((sum, p) => sum + (p.bid_amount || 0), 0);
  
  // Calculate aging buckets
  const agingBuckets = {
    current: projects.filter(p => getDaysOutstanding(p.invoice_date) <= 30),
    days31_60: projects.filter(p => getDaysOutstanding(p.invoice_date) > 30 && getDaysOutstanding(p.invoice_date) <= 60),
    days61_90: projects.filter(p => getDaysOutstanding(p.invoice_date) > 60 && getDaysOutstanding(p.invoice_date) <= 90),
    over90: projects.filter(p => getDaysOutstanding(p.invoice_date) > 90)
  };

  return (
    <div className="ml-64 p-8 min-h-screen bg-gradient-to-br from-[#0f172a] to-[#1e293b]">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">Accounts Receivable</h1>
            <p className="text-[#94a3b8]">Invoiced projects awaiting payment</p>
          </div>
          <div className="flex items-center gap-4">
            {filteredProjects.length > 0 && (
              <Button
                onClick={exportToExcel}
                variant="outline"
                className="border-[#94a3b8] text-[#94a3b8] hover:bg-[#1e293b]"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            )}
            <div className="bg-[#1e293b] p-4 rounded-lg border-2 border-[#f97316]">
              <div className="text-xs text-[#94a3b8] uppercase tracking-wider mb-1">Total Outstanding</div>
              <div className="text-2xl font-bold text-[#f97316]">{formatCurrency(totalAR)}</div>
            </div>
          </div>
        </div>

        {/* Aging Summary Cards */}
        {projects.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="millmetrix-card p-4 border-l-4 border-l-[#10b981]">
              <div className="text-xs text-[#94a3b8] uppercase tracking-wider mb-1">Current (0-30)</div>
              <div className="text-xl font-bold text-[#10b981]">
                {formatCurrency(agingBuckets.current.reduce((s, p) => s + (p.bid_amount || 0), 0))}
              </div>
              <div className="text-xs text-[#94a3b8]">{agingBuckets.current.length} invoice{agingBuckets.current.length !== 1 ? 's' : ''}</div>
            </Card>
            <Card className="millmetrix-card p-4 border-l-4 border-l-yellow-500">
              <div className="text-xs text-[#94a3b8] uppercase tracking-wider mb-1">31-60 Days</div>
              <div className="text-xl font-bold text-yellow-500">
                {formatCurrency(agingBuckets.days31_60.reduce((s, p) => s + (p.bid_amount || 0), 0))}
              </div>
              <div className="text-xs text-[#94a3b8]">{agingBuckets.days31_60.length} invoice{agingBuckets.days31_60.length !== 1 ? 's' : ''}</div>
            </Card>
            <Card className="millmetrix-card p-4 border-l-4 border-l-orange-500">
              <div className="text-xs text-[#94a3b8] uppercase tracking-wider mb-1">61-90 Days</div>
              <div className="text-xl font-bold text-orange-500">
                {formatCurrency(agingBuckets.days61_90.reduce((s, p) => s + (p.bid_amount || 0), 0))}
              </div>
              <div className="text-xs text-[#94a3b8]">{agingBuckets.days61_90.length} invoice{agingBuckets.days61_90.length !== 1 ? 's' : ''}</div>
            </Card>
            <Card className="millmetrix-card p-4 border-l-4 border-l-red-500">
              <div className="text-xs text-[#94a3b8] uppercase tracking-wider mb-1">Over 90 Days</div>
              <div className="text-xl font-bold text-red-500">
                {formatCurrency(agingBuckets.over90.reduce((s, p) => s + (p.bid_amount || 0), 0))}
              </div>
              <div className="text-xs text-[#94a3b8]">{agingBuckets.over90.length} invoice{agingBuckets.over90.length !== 1 ? 's' : ''}</div>
            </Card>
          </div>
        )}

        {/* Search and Sort */}
        {projects.length > 0 && (
          <Card className="millmetrix-card p-4 mb-6">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
                  Search
                </Label>
                <Input
                  type="text"
                  placeholder="Search by project, contractor, invoice #..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="millmetrix-input"
                />
              </div>
              <div className="w-40">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
                  Sort By
                </Label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="millmetrix-input w-full"
                >
                  <option value="invoice_date">Invoice Date</option>
                  <option value="days_outstanding">Days Outstanding</option>
                  <option value="project_name">Project Name</option>
                  <option value="contractor">Contractor</option>
                  <option value="amount">Amount</option>
                </select>
              </div>
              <div className="w-32">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
                  Order
                </Label>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="millmetrix-input w-full"
                >
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </select>
              </div>
            </div>
          </Card>
        )}

        {/* Projects List */}
        {projects.length === 0 ? (
          <Card className="millmetrix-card p-12 text-center">
            <Receipt className="w-16 h-16 text-[#94a3b8] mx-auto mb-4" />
            <p className="text-[#94a3b8] text-lg">No outstanding invoices</p>
            <p className="text-[#64748b] text-sm mt-2">All projects are up to date!</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredProjects.map((project) => {
              const daysOutstanding = getDaysOutstanding(project.invoice_date);
              const aging = getAgingCategory(daysOutstanding);
              
              return (
                <Card key={project.id} className="millmetrix-card p-6 hover:border-[#f97316] transition-all">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-white">
                          {project.project_name}
                        </h3>
                        <span className={`text-xs px-2 py-1 rounded ${aging.bg} ${aging.color}`}>
                          {daysOutstanding} days
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm mb-4">
                        <div>
                          <span className="text-[#94a3b8] block text-xs uppercase">Control #</span>
                          <span className="text-white font-mono">{project.control_number}</span>
                        </div>
                        <div>
                          <span className="text-[#94a3b8] block text-xs uppercase">Contractor</span>
                          <span className="text-white">{project.contractor_name}</span>
                        </div>
                        <div>
                          <span className="text-[#94a3b8] block text-xs uppercase">Invoice #</span>
                          <span className="text-white font-mono">{project.invoice_number || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-[#94a3b8] block text-xs uppercase">Invoice Date</span>
                          <span className="text-white">{formatDate(project.invoice_date)}</span>
                        </div>
                        <div>
                          <span className="text-[#94a3b8] block text-xs uppercase">Amount Due</span>
                          <span className="text-[#10b981] font-bold text-lg">{formatCurrency(project.bid_amount)}</span>
                        </div>
                      </div>
                      
                      <div className="text-xs text-[#64748b]">
                        Total LF: {(project.total_lf || 0).toLocaleString()} | Days on site: {project.days_on_site || 0}
                      </div>
                    </div>
                    
                    <div className="ml-4 flex flex-col gap-2">
                      <Button
                        onClick={() => openPaymentDialog(project)}
                        className="bg-[#10b981] hover:bg-[#059669] text-white"
                      >
                        <DollarSign className="w-4 h-4 mr-2" />
                        Record Payment
                      </Button>
                      <Button
                        onClick={() => setDeleteConfirm(project.id)}
                        variant="outline"
                        className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Payment Recording Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="bg-[#1e293b] text-white border-[rgba(255,255,255,0.1)] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#10b981]" />
              Record Payment
            </DialogTitle>
            <DialogDescription className="text-[#94a3b8]">
              Enter payment details for {selectedProject?.project_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Invoice Info */}
            <div className="bg-[#0f172a] p-4 rounded-lg border border-[rgba(255,255,255,0.05)]">
              <div className="flex justify-between items-center mb-2">
                <span className="text-[#94a3b8] text-sm">Invoice Amount</span>
                <span className="text-[#f97316] font-bold text-lg">
                  {formatCurrency(selectedProject?.bid_amount)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[#94a3b8] text-sm">Invoice Date</span>
                <span className="text-white text-sm">
                  {formatDate(selectedProject?.invoice_date)}
                </span>
              </div>
            </div>

            {/* Payment Date */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Payment Received Date
              </Label>
              <Input
                type="date"
                value={paymentData.payment_received_date}
                onChange={(e) => setPaymentData({ ...paymentData, payment_received_date: e.target.value })}
                className="millmetrix-input mt-2"
              />
            </div>

            {/* Payment Amount */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Payment Amount
              </Label>
              <Input
                type="number"
                step="0.01"
                value={paymentData.payment_amount}
                onChange={(e) => setPaymentData({ ...paymentData, payment_amount: e.target.value })}
                className="millmetrix-input mt-2"
                placeholder={selectedProject?.bid_amount?.toString()}
              />
              <p className="text-xs text-[#64748b] mt-1">Leave blank to use invoice amount</p>
            </div>

            {/* Payment Notes */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Payment Notes (Optional)
              </Label>
              <textarea
                value={paymentData.payment_notes}
                onChange={(e) => setPaymentData({ ...paymentData, payment_notes: e.target.value })}
                className="millmetrix-input mt-2 min-h-[60px] resize-none w-full"
                placeholder="Check #, wire transfer, etc."
              />
            </div>

            {/* Days to Pay Preview */}
            {selectedProject?.invoice_date && paymentData.payment_received_date && (
              <div className="bg-[#0f172a] p-3 rounded-lg border border-[rgba(255,255,255,0.05)]">
                <div className="flex justify-between items-center">
                  <span className="text-[#94a3b8] text-sm">Days to Payment</span>
                  <span className="text-[#10b981] font-bold">
                    {Math.floor((new Date(paymentData.payment_received_date) - new Date(selectedProject.invoice_date)) / (1000 * 60 * 60 * 24))} days
                  </span>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPaymentDialogOpen(false)}
              className="border-[#94a3b8] text-[#94a3b8] hover:bg-[#94a3b8] hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={markAsPaid}
              className="bg-[#10b981] hover:bg-[#059669] text-white"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Confirm Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="millmetrix-card p-6 max-w-md w-full">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 bg-red-500/20 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Delete Project?</h3>
                <p className="text-[#94a3b8]">
                  This action cannot be undone. The project and all associated data will be permanently deleted.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button
                onClick={() => setDeleteConfirm(null)}
                variant="outline"
                className="border-[#94a3b8] text-[#94a3b8] hover:bg-[#1e293b]"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleDeleteProject(deleteConfirm)}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Delete Project
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AccountsReceivable;
